import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { 
  Package, 
  ShoppingCart, 
  Users, 
  BarChart3, 
  Receipt, 
  Truck,
  ChevronLeft,
  ChevronRight,
  Check,
  ArrowRight
} from "lucide-react";

const ONBOARDING_KEY = "hesabaty_onboarding_complete";

const features = [
  {
    icon: Package,
    titleAr: "إدارة المخزون",
    titleEn: "Inventory Management",
    descAr: "تتبع منتجاتك وكمياتها بسهولة مع تنبيهات المخزون المنخفض",
    descEn: "Track your products and quantities easily with low stock alerts",
    color: "from-blue-500 to-cyan-500"
  },
  {
    icon: ShoppingCart,
    titleAr: "نقطة البيع",
    titleEn: "Point of Sale",
    descAr: "نظام بيع سريع وسهل مع طباعة الفواتير ومشاركتها",
    descEn: "Fast and easy sales system with invoice printing and sharing",
    color: "from-emerald-500 to-green-500"
  },
  {
    icon: Users,
    titleAr: "إدارة الزبائن والديون",
    titleEn: "Customer & Debt Management",
    descAr: "تتبع ديون الزبائن وإرسال تذكيرات تلقائية عبر واتساب",
    descEn: "Track customer debts and send automatic WhatsApp reminders",
    color: "from-purple-500 to-pink-500"
  },
  {
    icon: BarChart3,
    titleAr: "تقارير شاملة",
    titleEn: "Comprehensive Reports",
    descAr: "تقارير يومية وشهرية وسنوية مع تصدير PDF",
    descEn: "Daily, monthly, and yearly reports with PDF export",
    color: "from-amber-500 to-orange-500"
  },
  {
    icon: Receipt,
    titleAr: "إدارة المصاريف",
    titleEn: "Expense Management",
    descAr: "تتبع جميع مصاريفك ورواتب الموظفين",
    descEn: "Track all your expenses and employee salaries",
    color: "from-red-500 to-rose-500"
  },
  {
    icon: Truck,
    titleAr: "خدمة التوصيل",
    titleEn: "Delivery Service",
    descAr: "إدارة منتجات التوصيل ومشاركتها مع الزبائن",
    descEn: "Manage delivery products and share with customers",
    color: "from-indigo-500 to-violet-500"
  }
];

export default function OnboardingPage() {
  const { } = useAuth();
  const language = "ar";
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);

  const isArabic = language === "ar";

  useEffect(() => {
    // التحقق من إكمال Onboarding سابقاً
    const completed = localStorage.getItem(ONBOARDING_KEY);
    if (completed === "true") {
      navigate("/dashboard", { replace: true });
    }
  }, [navigate]);

  const handleNext = () => {
    if (currentStep < features.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      completeOnboarding();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    completeOnboarding();
  };

  const completeOnboarding = () => {
    localStorage.setItem(ONBOARDING_KEY, "true");
    setIsCompleted(true);
    setTimeout(() => {
      navigate("/dashboard", { replace: true });
    }, 500);
  };

  const currentFeature = features[currentStep];
  const Icon = currentFeature.icon;

  if (isCompleted) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center animate-fade-in-up">
          <div className="w-20 h-20 mx-auto bg-emerald-500 rounded-full flex items-center justify-center mb-4">
            <Check className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-white">
            {isArabic ? "مرحباً بك!" : "Welcome!"}
          </h2>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col items-center justify-center p-4">
      {/* Skip Button */}
      <Button
        variant="ghost"
        onClick={handleSkip}
        className="absolute top-4 left-4 text-slate-400 hover:text-white"
      >
        {isArabic ? "تخطي" : "Skip"}
      </Button>

      {/* Progress Dots */}
      <div className="flex gap-2 mb-8">
        {features.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentStep(index)}
            className={`w-2 h-2 rounded-full transition-all ${
              index === currentStep 
                ? "bg-emerald-500 w-8" 
                : index < currentStep 
                  ? "bg-emerald-500/50" 
                  : "bg-slate-700"
            }`}
          />
        ))}
      </div>

      {/* Feature Card */}
      <Card className="w-full max-w-md bg-slate-800/50 border-slate-700 overflow-hidden">
        <CardContent className="p-8 text-center">
          {/* Icon */}
          <div className={`w-24 h-24 mx-auto rounded-2xl bg-gradient-to-br ${currentFeature.color} flex items-center justify-center mb-6 shadow-lg animate-pulse-slow`}>
            <Icon className="w-12 h-12 text-white" />
          </div>

          {/* Title */}
          <h2 className="text-2xl font-bold text-white mb-4">
            {isArabic ? currentFeature.titleAr : currentFeature.titleEn}
          </h2>

          {/* Description */}
          <p className="text-slate-400 text-lg leading-relaxed">
            {isArabic ? currentFeature.descAr : currentFeature.descEn}
          </p>
        </CardContent>
      </Card>

      {/* Navigation */}
      <div className="flex items-center gap-4 mt-8">
        <Button
          variant="outline"
          size="icon"
          onClick={handlePrev}
          disabled={currentStep === 0}
          className="border-slate-700 text-slate-400 hover:text-white disabled:opacity-30"
        >
          {isArabic ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
        </Button>

        <Button
          onClick={handleNext}
          className="bg-emerald-600 hover:bg-emerald-700 px-8 py-6 text-lg"
        >
          {currentStep === features.length - 1 ? (
            <>
              {isArabic ? "ابدأ الآن" : "Get Started"}
              <ArrowRight className="w-5 h-5 ms-2" />
            </>
          ) : (
            <>
              {isArabic ? "التالي" : "Next"}
              {isArabic ? <ChevronLeft className="w-5 h-5 ms-2" /> : <ChevronRight className="w-5 h-5 ms-2" />}
            </>
          )}
        </Button>

        <Button
          variant="outline"
          size="icon"
          onClick={handleNext}
          disabled={currentStep === features.length - 1}
          className="border-slate-700 text-slate-400 hover:text-white disabled:opacity-30"
        >
          {isArabic ? <ChevronLeft className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        </Button>
      </div>

      {/* Step Counter */}
      <p className="text-slate-500 mt-4 text-sm">
        {currentStep + 1} / {features.length}
      </p>
    </div>
  );
}

// تصدير دالة للتحقق من إكمال Onboarding
export const isOnboardingComplete = () => {
  return localStorage.getItem(ONBOARDING_KEY) === "true";
};

// تصدير دالة لإعادة تشغيل Onboarding
export const resetOnboarding = () => {
  localStorage.removeItem(ONBOARDING_KEY);
};
